import { useState, useCallback } from "react";
import { AxiosError } from "axios";
// import apiClient from "../config/api";
import type { DjangoStyleNameVerificationData } from "../types";
import { useGeneralApiCall } from "@/services/useGeneralApiCall";

interface NameValidation {
  isValid: boolean;
  exists: boolean;
  message: string;
}

interface UseStyleNameValidationReturn {
  validation: NameValidation | null;
  isValidating: boolean;
  validateStyleName: (styleName: string) => Promise<void>;
  clearValidation: () => void;
}

export const useStyleNameValidation = (): UseStyleNameValidationReturn => {
  const [validation, setValidation] = useState<NameValidation | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  const { getApi } = useGeneralApiCall();

  const validateStyleName = useCallback(async (styleName: string) => {
    if (!styleName.trim()) {
      setValidation(null);
      return;
    }

    try {
      setIsValidating(true);
      // Use Django API endpoint for style name verification
      //   const response = await apiClient.get<DjangoStyleNameVerificationData>(
      //     `/style/v1/verify-style-name/${encodeURIComponent(styleName)}/`
      //   );

      const response = await getApi<DjangoStyleNameVerificationData>(
        `api/style/v1/verify-style-name/${encodeURIComponent(styleName)}/`
      );

      if (response.message === "No Style Name exists.") {
        setValidation({
          isValid: true,
          exists: false,
          message: response.message || "Style name is available",
        });
      } else {
        setValidation({
          isValid: false,
          exists: true,
          message: response.message || "Style name already exists",
        });
      }
    } catch (error) {
      console.error("Error validating style name:", error);

      // Check if it's an Axios error and if it's a 404 (style name doesn't exist - which means it's available)
      if (error instanceof Error && "response" in error) {
        const axiosError = error as AxiosError;
        if (axiosError.response?.status === 404) {
          setValidation({
            isValid: true,
            exists: false,
            message: "Style name is available",
          });
          return;
        }
      }

      setValidation({
        isValid: false,
        exists: false,
        message: "Style Name already Exists.",
      });
    } finally {
      setIsValidating(false);
    }
  }, []);

  const clearValidation = useCallback(() => {
    setValidation(null);
  }, []);

  return {
    validation,
    isValidating,
    validateStyleName,
    clearValidation,
  };
};
